'use client';

import 'keen-slider/keen-slider.min.css';

import { Text } from '@/components/_rebrand/Text';
import ThemeWrapper from '@/components/_rebrand/ThemeWrapper';
import { ThemeNameType } from '@/tailwind-config/theme.constants';
import { useKeenSlider } from 'keen-slider/react';
import { FC, useRef } from 'react';

import { createAutoplayPlugin } from '../keenSliderPlugins/autoplayPlugin';
import { createFocusPlugin } from '../keenSliderPlugins/focusPlugin';
import { createWheelControlsPlugin } from '../keenSliderPlugins/wheelControlsPlugin';
import { TestimonialCard, type TestimonialCardProps } from './TestimonialCard';
import { sliderOptions } from './TestimonialCards.constants';

export type TestimonialCardsProps = {
  title: string;
  cards: TestimonialCardProps[];
  theme?: ThemeNameType;
};

export const TestimonialCards: FC<TestimonialCardsProps> = ({ title, cards = [], theme }) => {
  const autoplayPlugin = useRef(createAutoplayPlugin({ enabledMediaQuery: '(min-width: 600px)' })).current;
  const wheelControlsPlugin = useRef(createWheelControlsPlugin()).current;
  const focusPlugin = useRef(createFocusPlugin()).current;

  const [sliderRef] = useKeenSlider(sliderOptions, [autoplayPlugin, wheelControlsPlugin, focusPlugin]);

  return (
    <ThemeWrapper theme={theme || 'theme-gray'}>
      <div className="surface-l0 py-3xl flex flex-col items-center gap-2xl overflow-hidden">
        <Text type="h3" as="h3" className="text-primary text-center text-pretty w-xl">{title}</Text>

        <div className="relative w-full">
          <div ref={sliderRef} className="keen-slider overflow-visible cursor-grab active:cursor-grabbing">
            {cards.map((card) => (
              <TestimonialCard key={card.authorName} {...card} className="keen-slider__slide" />
            ))}
          </div>

          <div
            className="hidden sm:block absolute -inset-y-2 left-0 w-24 surface-l0 pointer-events-none z-10"
            style={{
              maskImage: 'linear-gradient(to right, black, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, black, transparent)',
            }}
          />
          <div
            className="hidden sm:block absolute -inset-y-2 right-0 w-24 surface-l0 pointer-events-none z-10"
            style={{
              maskImage: 'linear-gradient(to left, black, transparent)',
              WebkitMaskImage: 'linear-gradient(to left, black, transparent)',
            }}
          />
        </div>
      </div>
    </ThemeWrapper>
  );
};
